import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { QRCodeSVG } from 'qrcode.react';

const JobCard = ({ job, onSave, onApply, onViewDetails, isSaved }) => {
  const [showQR, setShowQR] = React.useState(false);
  const [copiedLink, setCopiedLink] = React.useState(false);

  const formatSalary = (min, max) => {
    return `₹${(min / 100000)?.toFixed(1)}L - ₹${(max / 100000)?.toFixed(1)}L`;
  };

  const getTimeAgo = (date) => {
    const now = new Date();
    const posted = new Date(date);
    const diffInHours = Math.floor((now - posted) / (1000 * 60 * 60));

    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return `${Math.floor(diffInDays / 7)}w ago`;
  };

  const parseSkills = (skills) => {
    if (!skills) return [];
    if (Array.isArray(skills)) {
      return skills.flatMap(s => {
        if (typeof s !== 'string') return [];
        if (s.includes(',')) return s.split(',').map(item => item.trim());
        return [s.trim()];
      }).filter(Boolean);
    }
    if (typeof skills === 'string') {
      if (skills.includes(',')) {
        return skills.split(',').map(s => s.trim()).filter(Boolean);
      }
      return skills.split(/\s+/).map(s => s.trim()).filter(Boolean);
    }
    return [];
  };

  const skillList = parseSkills(job?.skills);

  const handleCopyShareLink = () => {
    const directUrl = `${window.location.origin}/apply/${job?.id}`;
    navigator.clipboard.writeText(directUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 3000);
  };

  const handleDownloadQR = () => {
    const svg = document.querySelector(`#qr-${job?.id} svg`);
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 200;
    const ctx = canvas.getContext('2d');
    const img = new window.Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0);
      const link = document.createElement('a');
      link.download = `${job?.title || 'job'}-qr.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  return (
    <div className="bg-card rounded-lg p-4 md:p-6 border border-border hover:shadow-lg transition-all duration-300">
      {/* Top Header Row: Title & Company on left, Action icons on right */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg md:text-xl font-semibold text-foreground mb-0.5 line-clamp-1">
            {job?.title}
          </h3>
          <p className="text-sm md:text-base text-muted-foreground">
            {job?.company}
          </p>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={handleCopyShareLink}
            className="px-2.5 py-1.5 rounded-md hover:bg-muted flex items-center gap-1 text-xs font-medium text-primary transition-colors border border-primary/20 bg-primary/5"
            title="Copy shareable application link for candidate"
          >
            <Icon name={copiedLink ? "Check" : "Share2"} size={14} />
            <span>{copiedLink ? "Link Copied!" : "Share Link"}</span>
          </button>

          <button
            onClick={() => onSave(job?.id)}
            className="w-9 h-9 rounded-md hover:bg-muted flex items-center justify-center transition-colors"
            aria-label={isSaved ? 'Unsave job' : 'Save job'}
          >
            <Icon
              name={isSaved ? 'Bookmark' : 'Bookmark'}
              size={20}
              color={isSaved ? 'var(--color-accent)' : 'currentColor'}
            />
          </button>

          <button
            onClick={() => setShowQR(true)}
            className="w-9 h-9 rounded-md hover:bg-muted flex items-center justify-center transition-colors"
            aria-label="Generate QR code"
          >
            <Icon name="QrCode" size={20} />
          </button>
        </div>
      </div>

      {/* Metadata Badges */}
      <div className="flex flex-wrap gap-2 mb-2.5">
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs md:text-sm font-medium">
          <Icon name="Briefcase" size={14} />
          {job?.experience}
        </span>
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-success/10 text-success text-xs md:text-sm font-medium">
          <Icon name="IndianRupee" size={14} />
          {formatSalary(job?.salaryMin, job?.salaryMax)}
        </span>
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-muted text-muted-foreground text-xs md:text-sm font-medium">
          <Icon name="MapPin" size={14} />
          {job?.location}
        </span>
      </div>

      {/* Job Description */}
      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
        {job?.description}
      </p>

      {/* Skill Tags Pill Badges */}
      {skillList.length > 0 && (
        <div className="flex flex-wrap gap-1.5 md:gap-2 mb-4">
          {skillList.map((skill, index) => (
            <span
              key={index}
              className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/20 text-xs font-medium transition-colors"
            >
              {skill}
            </span>
          ))}
        </div>
      )}

      {/* Bottom Footer Row */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-1">
        <div className="flex items-center gap-4 text-xs md:text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <Icon name="Clock" size={14} />
            {getTimeAgo(job?.postedDate)}
          </span>
          <span className="flex items-center gap-1">
            <Icon name="Users" size={14} />
            {job?.applicants} applicants
          </span>
        </div>

        <div className="flex gap-2 w-full sm:w-auto">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 sm:flex-none"
            onClick={() => onViewDetails(job)}
          >
            View Details
          </Button>
          {job?.has_applied ? (
            <button
              disabled
              className="flex-1 sm:flex-none px-4 py-2 rounded-lg bg-slate-800/80 text-slate-400 font-semibold text-xs border border-slate-700 cursor-not-allowed flex items-center justify-center gap-1.5 shadow-inner"
              title="You have already applied for this position"
            >
              <Icon name="CheckCircle2" size={14} className="text-emerald-500" />
              <span>Already Applied</span>
            </button>
          ) : (
            <Button
              variant="default"
              size="sm"
              className="flex-1 sm:flex-none"
              onClick={() => onApply(job)}
            >
              Apply Now
            </Button>
          )}
        </div>
      </div>

      {/* QR Code Modal */}
      {showQR && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowQR(false)}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-xs w-full text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-semibold text-foreground mb-1">{job?.title}</h3>
            <p className="text-sm text-muted-foreground mb-4">Scan to apply</p>
            <div className="flex justify-center mb-4" id={`qr-${job?.id}`}>
              <QRCodeSVG
                value={`${window.location.origin}/apply/${job?.id}`}
                size={200}
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="flex-1" onClick={() => setShowQR(false)}>
                Close
              </Button>
              <Button variant="default" size="sm" className="flex-1" onClick={handleDownloadQR}>
                Download
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobCard;